import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { InputField } from "../../../components/Form/InputField";
import { useEffect, useState } from "react";
import { validationSchema } from "../Validation/registrationValidation";
import { createRegistration, sendOtp } from "../../../service/Auth/authService";
import {
  notifyError,
  notifySuccess,
} from "../../../components/ToastContainer/Toast/Toast";
import { useNavigate } from "react-router-dom";
import PolicyTextLink from "../../../components/Policy/PolicyTextLink";
import { cipher } from "../../../utils/crypto";
import { PARTNER_PRIMARY_HEX } from "../../../constants/partnerBrand";
import { isPartnerAuthLayout } from "../../../config/sellerOrgConfig";
import { useSellerOrgConfig } from "../../../Hooks/useSellerOrgConfig";

export interface RegisterationFormProps {
  /** @deprecated Layout is resolved via seller org config; prop is ignored. */
  embeddedInPartnerLayout?: boolean;
}

const RegisterationForm: React.FC<RegisterationFormProps> = () => {
  const [mobileNumber, setMobileNumber] = useState("");
  const [showOtpField, setShowOtpField] = useState(false);
  const [timer, setTimer] = useState(0);
  const [otpValue, setOtpValue] = useState("");
  const [isFirstTimeOtp, setIsFirstTimeOtp] = useState(true);
  // Sticky flag: keep the Verify & Continue button disabled once the server
  // has accepted the registration, so a fast double-click during the tiny
  // window before navigate() runs cannot fire a second POST /auth/register.
  const [hasSubmittedSuccessfully, setHasSubmittedSuccessfully] = useState(false);

  const [initialValues] = useState({
    phone_number: "",
    email: "",
    business_type: "nonGst",
    gst_number: "",
    receive_message: false,
    otp: "",
  });

  const navigate = useNavigate();
  const orgConfig = useSellerOrgConfig();
  const embeddedInPartnerLayout = isPartnerAuthLayout(orgConfig);
  const { copy } = orgConfig.auth;
  const { branding } = orgConfig;
  const termsUrl = branding.termsUrl?.trim() || "";
  const privacyUrl = branding.privacyUrl?.trim() || "";
  const policyLinkSx = {
    "&::before": { display: "none" },
    fontWeight: 700,
  } as const;
  const handleRegisterSubmit = async (values, { resetForm }) => {
    if (!showOtpField) {
      notifyError("OTP verification is required before proceeding.");
      return;
    }
    if (!otpValue) {
      notifyError("Please enter the OTP to continue.");
      return;
    }

    const postPayLoad = {
      email: values.email,
      phone_number: values.phone_number,
      otp: otpValue,
      gst: values.business_type === "gst" ? true : false,
      ...(values.business_type === "gst" && { gst_no: values.gst_number }),
    };

    try {
      const response = await createRegistration(postPayLoad);

      if ([200, 201].includes(response.status)) {
        const userIdAndPassword = response.data.data;
        const convertUserIdAndPassword = {
          ...userIdAndPassword,
          user_id: cipher(userIdAndPassword?.user_id.toString()),
          password: cipher(userIdAndPassword?.password),
          gst: postPayLoad.gst,
          email: cipher(postPayLoad.email),
          phone_number: cipher(postPayLoad.phone_number),
          ...(postPayLoad.gst === true && {
            gst_no: cipher(postPayLoad.gst_no),
          }),
        };
        if (
          Object.values(userIdAndPassword).length > 0 &&
          Object.values(convertUserIdAndPassword).length > 0 &&
          localStorage.getItem("lastPath") === "/registration"
        ) {
          localStorage.setItem(
            "userRegistration",
            JSON.stringify(convertUserIdAndPassword)
          );
        }
        setHasSubmittedSuccessfully(true);
        resetForm();
        navigate("/register", {
          state: {
            isRegisteredState: true,
          },
        });
        if (localStorage.getItem("lastPath") === "/registration") {
          localStorage.setItem("isRegisteredState", JSON.stringify(true));
        }
      }
    } catch (error) {
      notifyError(
        error?.response?.data?.message ||
          error?.message ||
          "Something Went Wrong"
      );
    }
  };

  const currentIndex = window.history.state?.idx || 0;
  useEffect(() => {
  
    const handlePopState = () => {
      const newIndex = window.history.state?.idx || 0;
  
      if (newIndex > currentIndex) {
        window.history.back();
      }
    };
  
    window.addEventListener("popstate", handlePopState);
    handlePopState();
    return () => window.removeEventListener("popstate", handlePopState);
  }, [currentIndex]);
  
  

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    if (showOtpField && otpValue === "" && timer > 0 && isFirstTimeOtp) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [timer, showOtpField, otpValue, isFirstTimeOtp]);

  const handleGetOtp = async () => {
    if (!mobileNumber) return;

    try {
      const response = await sendOtp({
        phone_number: mobileNumber,
        purpose: "registration",
      });

      if (response.status === 201) {
        if (response.data?.success) {
          setShowOtpField(true);
          setOtpValue("");
          setIsFirstTimeOtp(true);
          setTimer(30);
          notifySuccess(response.data?.message || "OTP sent successfully");
        } else {
          notifyError(response.data?.message || "Failed to send OTP");
        }
      }
    } catch (error) {
      notifyError(
        error?.response?.data?.message || error?.message || "Failed to send OTP"
      );
    }
  };

  // const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e.target.value.trim();
  //   setOtpValue(value);
  //   if (value !== "") setTimer(0);
  // };

  const handleResendOtp = async () => {
    if (!mobileNumber) return;

    try {
      const response = await sendOtp({
        phone_number: mobileNumber,
        purpose: "registration",
      });
      if (response.status === 201) {
        if (response.data?.success) {
          setOtpValue("");
          setTimer(30);
          setIsFirstTimeOtp(true);
          setShowOtpField(true);
          notifySuccess(response.data?.message || "OTP sent successfully");
        } else {
          notifyError(response.data?.message || "Failed to resend OTP");
        }
      }
    } catch (error) {
      notifyError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to resend OTP"
      );
    }
  };

  const handleMobileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue,
    setFieldTouched
  ) => {
    const { name, value } = e.target;
    setMobileNumber(value.trim());
    setFieldValue(name, value.trim());

    if (showOtpField) {
      setShowOtpField(false);
      setOtpValue("");
      setIsFirstTimeOtp(false);
      setTimer(0);
    }

    setTimeout(() => {
      setFieldTouched(name, true, true);
    }, 0);
  };

  const fieldLabelSx = embeddedInPartnerLayout
    ? {
        fontWeight: 800,
        fontSize: "10px",
        letterSpacing: "0.14em",
        textTransform: "uppercase" as const,
        color: PARTNER_PRIMARY_HEX,
        mb: "8px",
        ml: 0.5,
      }
    : {
        fontWeight: 500,
        fontSize: "clamp(0.625rem, 0.5585rem + 0.2128vw, 0.75rem)",
        mb: "8px",
      };

  return (
    <Box>
      {embeddedInPartnerLayout ? (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
              fontWeight: 800,
              fontSize: "1.75rem",
              letterSpacing: "-0.02em",
              mb: 0.5,
            }}
          >
            {copy.partnerRegistrationTitle || copy.registrationTitle}
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "rgba(21, 28, 39, 0.65)", lineHeight: 1.5 }}>
            {copy.partnerRegistrationSubtitle || copy.registrationSubtitle}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              textWrap: "pretty",
              fontWeight: 700,
              fontSize: "clamp(18px, 2vw, 24px)",
              lineHeight: "150%",
              textAlign: "center",
            }}
          >
            {copy.registrationTitle}
          </Typography>
          <Typography
            sx={{
              mt: { xs: 0.5, sm: 1 },
              textWrap: "pretty",
              fontSize: "clamp(10px, 1.5vw, 13px)",
              fontWeight: 500,
              textAlign: "center",
              lineHeight: "150%",
              color: "rgba(0, 0, 0, 0.5)",
            }}
          >
            {copy.registrationSubtitle}
          </Typography>
        </Box>
      )}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleRegisterSubmit}
      >
        {({ values, handleChange, errors, setFieldValue, setFieldTouched, isSubmitting }) => (
          <Form>
            <Box sx={{ mt: embeddedInPartnerLayout ? 0 : { xs: 1.5, sm: 2 } }}>
              <Box>
                <Typography sx={fieldLabelSx}>Mobile Number</Typography>
                <InputField
                  name="phone_number"
                  label=""
                  placeholder="94585 874561"
                  height={embeddedInPartnerLayout ? "52px" : "50px"}
                  is_phone={true}
                  value={mobileNumber}
                  onChange={(e) =>
                    handleMobileChange(e, setFieldValue, setFieldTouched)
                  }
                  endAdornment={
                    mobileNumber?.length === 10 && !showOtpField ? (
                      <Typography
                        sx={{
                          fontSize: "clamp(10px, 1.3vw, 11px)",
                          fontWeight: 700,
                          color: embeddedInPartnerLayout ? PARTNER_PRIMARY_HEX : "primary.main",
                          cursor: "pointer",
                        }}
                        onClick={handleGetOtp}
                      >
                        Get OTP
                      </Typography>
                    ) : null
                  }
                />

                {showOtpField && (
                  <Box sx={{ mt: { xs: 1, sm: 2 } }}>
                    <Typography sx={embeddedInPartnerLayout ? fieldLabelSx : { fontWeight: 500, fontSize: "12px", mb: "8px" }}>
                      Enter OTP
                    </Typography>
                    <InputField
                      name="otp"
                      label=""
                      placeholder="Enter OTP"
                      height={embeddedInPartnerLayout ? "52px" : "50px"}
                      value={otpValue}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const { value } = e.target;
                        setOtpValue(value.trim());

                        setFieldValue("otp", value.trim());
                        setTimeout(() => {
                          setFieldTouched("otp", true, true);
                        }, 0);

                        if (value.trim() !== "") setTimer(0);
                      }}
                    />

                    {otpValue === "" && isFirstTimeOtp && (
                      <Box sx={{ mt: 1 }}>
                        {timer > 0 ? (
                          <Typography
                            sx={{ fontSize: "clamp(12px, 1.4vw, 14px)" }}
                          >
                            {/* Resend OTP in {timer}s */}
                            Resend OTP in
                            <Box
                              component="span"
                              sx={{ color: "black", fontWeight: "bold" }}
                            >
                              {timer}s
                            </Box>
                          </Typography>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "clamp(12px, 1.4vw, 14px)",
                              fontWeight: 600,
                              color: embeddedInPartnerLayout ? PARTNER_PRIMARY_HEX : "primary.main",
                              cursor: "pointer",
                            }}
                            onClick={handleResendOtp}
                          >
                            Resend OTP
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <Box sx={{ mt: "10px" }}>
                <Typography sx={fieldLabelSx}>Email</Typography>
                <InputField
                  name="email"
                  label=""
                  placeholder={copy.emailPlaceholder ?? "eg: your@restaurant.com"}
                  height={embeddedInPartnerLayout ? "52px" : "50px"}
                  onChange={(e) => {
                    const { name, value } = e.target;
                    setFieldValue(name, value.trim());
                    setTimeout(() => {
                      setFieldTouched(name, true, true);
                    }, 0);
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: "10px",
                  mb: "10px",
                }}
              >
                <Typography
                  sx={{
                    ...(embeddedInPartnerLayout
                      ? fieldLabelSx
                      : {
                          fontSize: "clamp(10px, 1.3vw, 11px)",
                          fontWeight: 500,
                          lineHeight: "150%",
                          color: "rgba(26, 26, 26, 1)",
                        }),
                  }}
                >
                  Business Type
                </Typography>
                <Box sx={{ display: "flex" }}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={values.business_type}
                      onChange={(e) => {
                        setFieldValue("business_type", e.target.value);
                      }}
                    >
                      <FormControlLabel
                        value={"gst"}
                        control={
                          <Radio
                            sx={{
                              color: embeddedInPartnerLayout ? PARTNER_PRIMARY_HEX : "black",
                              "&.Mui-checked": {
                                color: embeddedInPartnerLayout ? PARTNER_PRIMARY_HEX : "black",
                              },
                            }}
                          />
                        }
                        label={"GST"}
                        sx={{
                          "& .MuiTypography-root": {
                            fontSize: "clamp(10px, 1.3vw, 11px)",
                            fontWeight: 400,
                            lineHeight: "130%",
                            color: "rgba(26, 26, 26, 1)",
                          },
                        }}
                      />
                      <FormControlLabel
                        value={"nonGst"}
                        control={
                          <Radio
                            sx={{
                              color: embeddedInPartnerLayout ? PARTNER_PRIMARY_HEX : "black",
                              "&.Mui-checked": {
                                color: embeddedInPartnerLayout ? PARTNER_PRIMARY_HEX : "black",
                              },
                            }}
                          />
                        }
                        label={"NON-GST"}
                        sx={{
                          "& .MuiTypography-root": {
                            fontSize: "clamp(10px, 1.3vw, 11px)",
                            fontWeight: 400,
                            lineHeight: "130%",
                            color: "rgba(26, 26, 26, 1)",
                          },
                        }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>

              {values.business_type === "gst" && (
                <Box sx={{ mb: "10px" }}>
                  <Typography
                    sx={
                      embeddedInPartnerLayout
                        ? fieldLabelSx
                        : {
                            fontWeight: 500,
                            fontSize: "clamp(10px, 1.3vw, 11px)",
                            mb: "8px",
                            lineHeight: "150%",
                          }
                    }
                  >
                    GST Number
                  </Typography>
                  <InputField
                    name={"gst_number"}
                    label={""}
                    placeholder="eg: 1254HUY5467"
                    onChange={(e) => {
                      const { name, value } = e.target;
                      setFieldValue(name, value.trim().toUpperCase());
                      setTimeout(() => {
                        setFieldTouched(name, true, true);
                      }, 0);
                    }}
                  />
                </Box>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    name="receive_message"
                    disableRipple
                    checked={values.receive_message}
                    onChange={handleChange}
                  />
                }
                label="I want to receive important updates on Email & Whatsapp"
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: "clamp(10px, 1.3vw, 11px)",
                    fontWeight: 500,
                    lineHeight: "150%",
                  },
                }}
              />
            </Box>
            <Box sx={{ mt: embeddedInPartnerLayout ? 0 : { xs: 1.5, sm: 2 }, mb: embeddedInPartnerLayout ? { xs: 2, sm: 3 } : 0 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={isSubmitting || hasSubmittedSuccessfully}
                sx={{
                  borderRadius: embeddedInPartnerLayout ? "12px" : "10px",
                  py: embeddedInPartnerLayout ? 1.75 : undefined,
                  fontWeight: embeddedInPartnerLayout ? 700 : 500,
                  textTransform: embeddedInPartnerLayout ? "none" : undefined,
                  fontSize: "clamp(0.75rem, 0.6835rem + 0.2128vw, 0.875rem)",
                  ...(embeddedInPartnerLayout
                    ? {
                        backgroundColor: PARTNER_PRIMARY_HEX,
                        color: "#fff",
                        boxShadow: "0 10px 24px rgba(0,110,47,0.25)",
                        "&:hover": { backgroundColor: "#004b1e" },
                      }
                    : {
                        backgroundColor: "rgba(26, 26, 26, 1)",
                      }),
                  "&.Mui-disabled": {
                    backgroundColor: embeddedInPartnerLayout
                      ? "rgba(0, 110, 47, 0.45)"
                      : "rgba(26, 26, 26, 0.45)",
                    color: "rgba(255, 255, 255, 0.75)",
                    cursor: "not-allowed",
                    pointerEvents: "auto",
                  },
                }}
                onClick={(e) => {
                  if (!showOtpField) {
                    e.preventDefault();
                    notifyError(
                      "OTP verification is required before proceeding."
                    );
                  } else if (!otpValue) {
                    e.preventDefault();
                    notifyError("Please enter the OTP to continue.");
                  }
                }}
              >
                {isSubmitting ? "Verifying..." : "Verify & Continue"}
              </Button>

              <Box
                textAlign="center"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 0.2,
                  mt: "10px",
                }}
              >
                <Typography
                  align="center"
                  sx={{
                    fontSize: "clamp(10px, 1.3vw, 11px)",
                    lineHeight: "150%",
                    fontWeight: 400,
                    color: "rgba(0, 0, 0, 0.5)",
                  }}
                >
                  By Continuing, you agree our
                  <PolicyTextLink
                    url={termsUrl}
                    label="Terms and Conditions"
                    sx={{ ...policyLinkSx, ml: 0.5, mr: 0.5 }}
                  />
                  and
                  <PolicyTextLink
                    url={privacyUrl}
                    label="privacy policy"
                    sx={{ ...policyLinkSx, ml: 0.5 }}
                  />
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                align="center"
                sx={{
                  textAlign: "center",
                  color: "rgba(0, 0, 0, 0.5)",
                  fontWeight: 400,
                  lineHeight: "150%",
                  fontSize: "clamp(10px, 1.3vw, 11px)",
                }}
              >
                Already Have an Account ?
                <Link
                  href={"/"}
                  variant="caption"
                  sx={{
                    "&::before": { display: "none" },
                    ml: 0.5,
                    fontWeight: 700,
                    ...(embeddedInPartnerLayout && { color: PARTNER_PRIMARY_HEX }),
                  }}
                >
                  Login Here
                </Link>
              </Typography>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default RegisterationForm;
